/**
 * Default maximum number of characters passed to the Prism tokenizer.
 */
const DEFAULT_MAX_TOKENIZATION_LENGTH = 100_000;

interface SplitResult {
  /** Code that Prism will tokenize (syntax-highlighted). */
  highlightCode: string;
  /**
   * Plain text that continues the last highlighted line (mid-line cut).
   * Should be rendered inside the same visual line as the last highlighted
   * token so that no extra line number is created.
   */
  noHighlightInline: string;
  /** Plain-text block of complete lines that follow the highlighted portion. */
  noHighlightCode: string;
  /** `\n`-separated line numbers for {@link noHighlightCode}. */
  noHighlightLineNumbers: string;
}

/**
 * Count newline characters in `str`.
 *
 * Uses `codePointAt` instead of `=== '\n'` for performance – avoids
 * per-character string allocation when iterating over multi-megabyte strings.
 */
function countNewlines(str: string): number {
  let count = 0;
  for (let i = 0; i < str.length; i++) {
    if (str.codePointAt(i) === 10) {
      count++;
    }
  }
  return count;
}

/**
 * Build a `\n`-separated string of consecutive line numbers.
 */
function buildLineNumbers(startLine: number, lineCount: number): string {
  const numbers = new Array<string>(lineCount);
  for (let i = 0; i < lineCount; i++) {
    numbers[i] = String(startLine + i);
  }
  return numbers.join('\n');
}

/**
 * Splits `code` so only the first portion is tokenized by Prism.
 *
 * Always cuts at `maxLength`. The remainder is split at its first newline:
 * text before that newline continues the last highlighted line
 * (`noHighlightInline`), text after it forms a separate plain-text block
 * (`noHighlightCode`) with its own line numbers.
 *
 * @param code - The full code string.
 * @param maxLength - Maximum number of characters to highlight.
 *   Defaults to {@link DEFAULT_MAX_TOKENIZATION_LENGTH}.
 *
 * @internal
 */
export function splitCodeForHighlighting(
  code: string,
  maxLength: number = DEFAULT_MAX_TOKENIZATION_LENGTH,
): SplitResult {
  // If the code is short enough, there is no need for splitting, highlight it all.
  if (code.length <= maxLength) {
    return {
      highlightCode: code,
      noHighlightInline: '',
      noHighlightCode: '',
      noHighlightLineNumbers: '',
    };
  }

  const highlight = code.substring(0, maxLength);
  const remaining = code.substring(maxLength);

  // Split the remainder at the first newline: text before it continues the
  // last highlighted line (inline), text after it forms a separate block.
  const firstNewline = remaining.indexOf('\n');
  const inlinePart =
    firstNewline < 0 ? remaining : remaining.substring(0, firstNewline);
  const blockPart =
    firstNewline < 0 ? '' : remaining.substring(firstNewline + 1);

  // Edge case: if there is no newline in the remainder,
  // then all the remaining text is inline (e.g. in case of a single long line).
  if (blockPart.length === 0) {
    return {
      highlightCode: highlight,
      noHighlightInline: inlinePart,
      noHighlightCode: '',
      noHighlightLineNumbers: '',
    };
  }

  // line count is 1 more than the number of newlines
  const highlightedLineCount = countNewlines(highlight) + 1;
  // line number of the first line in the non-highlighted block
  const startLine = highlightedLineCount + 1;
  const blockLineCount = countNewlines(blockPart) + 1;

  return {
    highlightCode: highlight,
    noHighlightInline: inlinePart,
    noHighlightCode: blockPart,
    noHighlightLineNumbers: buildLineNumbers(startLine, blockLineCount),
  };
}
